import {
  academicPlanPages,
  actionLogsPage,
  assignedStudentsPages,
  attendanceReportPages,
  chatRoomPages,
  classPages,
  communicationLog,
  communicationPages,
  counselorInformationPages,
  extendedExportationPage,
  gradeScalePages,
  graduationCategoryPages,
  graduationStudentsPages,
  individualAssessment,
  overallAssessment,
  programPages,
  progressReportPages,
  rolePages,
  roomMeetingPages,
  schedulePages,
  schoolParameterPages,
  scoreReportPages,
  staffPages,
  studentPages,
  subjectPages,
  surveyPages,
  taskManagementPage,
  termPages
} from "@/routes/routes";

import {
  All,
  AttendanceReport,
  Calendar,
  Class,
  Communication,
  CommunicationLog,
  ExtendedExportation,
  GradeScale,
  GraduationCategory,
  GraduationStudents,
  IndividualAssessment,
  OverallAssessment,
  Program,
  ProgressReport,
  RolePage,
  ScoreReport,
  Staff,
  Student,
  Subject,
  Survey,
  Term,
  ZoomMeeting
} from "@/constants/Permissions";

import Role from "@/constants/Role";
import { can } from "@/helpers/permission";
import { hasValidRole } from "@/helpers/role-helper";
import { getId } from "@/helpers/utils";
import { store } from "@/store";

export const SideBarItemCfgs = [
  {
    name: "sidebar.dashboard",
    icon: "dashboard",
    hasPermission: () => {
      return true;
    },
    generatePath: opt => "/dashboard"
  },
  {
    name: "sidebar.children_information",
    icon: "person",
    hasPermission: userRole => {
      return hasValidRole(userRole, Role.Family);
    },
    generatePath: opt => {
      const { childUser } = opt;
      const id = getId(childUser);
      return `${studentPages.path}/${id}/detail`;
    }
  },
  {
    name: "sidebar.staffs",
    icon: "people",
    hasPermission: () => {
      return can(Staff.actions.list, Staff.subject);
    },
    generatePath: opt => staffPages.path
  },
  {
    name: "sidebar.students",
    icon: "perm_contact_calendar",
    hasPermission: () => {
      return can(Student.actions.list, Student.subject);
    },
    generatePath: opt => studentPages.path
  },
  {
    name: "sidebar.terms",
    icon: "bookmark",
    hasPermission: () => {
      return can(Term.actions.list, Term.subject);
    },
    generatePath: opt => termPages.path
  },
  {
    name: "sidebar.classes",
    icon: "class",
    hasPermission: () => {
      return can(Class.actions.list, Class.subject);
    },
    generatePath: opt => classPages.path
  },
  {
    name: "sidebar.transcript",
    icon: "school",
    hasPermission: userRole => {
      return hasValidRole(userRole, [Role.Student, Role.Family]);
    },
    generatePath: opt => {
      const { rootUserRole } = opt;

      if (hasValidRole(rootUserRole, Role.Student)) {
        const { rootUser } = opt;
        return `${studentPages.path}/${getId(rootUser)}/transcript`;
      }

      const { childUser } = opt;
      return `${studentPages.path}/${getId(childUser)}/transcript`;
    }
  },
  {
    name: "sidebar.assigned_students",
    icon: "receipt",
    hasPermission: userRole => {
      return hasValidRole(userRole, [Role.Teacher, Role.Counselor]);
    },
    generatePath: opt => assignedStudentsPages.path
  },
  {
    name: "sidebar.academic_plan",
    icon: "event",
    hasPermission: userRole => {
      return hasValidRole(userRole, [Role.Family, Role.Student]);
    },
    generatePath: opt => academicPlanPages.path
  },
  {
    name: "sidebar.programs",
    icon: "menu",
    hasPermission: () => {
      return can(Program.actions.list, Program.subject);
    },
    generatePath: opt => programPages.path
  },
  {
    name: "sidebar.grade_scale",
    icon: "assessment",
    hasPermission: () => {
      return can(GradeScale.actions.list, GradeScale.subject);
    },
    generatePath: opt => gradeScalePages.path
  },
  {
    name: "sidebar.calendar",
    icon: "date_range",
    hasPermission: () => {
      return can(Calendar.actions.list, Calendar.subject);
    },
    generatePath: opt => schedulePages.path
  },
  {
    name: "sidebar.communication",
    icon: "forum",
    hasPermission: () => {
      return (
        can(Communication.actions.sendAnnouncement, Communication.subject) ||
        can(Communication.actions.sendMail, Communication.subject) ||
        can(Communication.actions.template, Communication.subject)
      );
    },
    generatePath: opt => communicationPages.path
  },
  {
    name: "sidebar.zoom_meeting",
    icon: "computer",
    hasPermission: userRole => {
      return (
        can(ZoomMeeting.actions.list, ZoomMeeting.subject) ||
        hasValidRole(userRole, Role.Teacher)
      );
    },
    generatePath: opt => roomMeetingPages.path
  },
  {
    name: "sidebar.chat_room",
    icon: "chat",
    hasPermission: userRole => {
      return !hasValidRole(userRole, Role.Family);
    },
    generatePath: opt => chatRoomPages.path
  },
  {
    name: "sidebar.subjects",
    icon: "account_balance_wallet",
    hasPermission: () => {
      return can(Subject.actions.list, Subject.subject);
    },
    generatePath: opt => subjectPages.path
  },
  {
    name: "sidebar.counselor_information",
    icon: "receipt",
    hasPermission: userRole => {
      return hasValidRole(userRole, [Role.Student, Role.Family]);
    },
    generatePath: opt => counselorInformationPages.path
  },

  /* Graduation */
  {
    name: "sidebar.graduation",
    icon: "school",
    group: "graduation",
    hasPermission: () => {
      return (
        can(GraduationCategory.actions.list, GraduationCategory.subject) ||
        can(GraduationStudents.actions.list, GraduationStudents.subject)
      );
    },
    generatePath: opt => graduationCategoryPages.path
  },
  {
    name: "sidebar.graduation_categories",
    icon: "chevron_right",
    groupName: "graduation",
    hasPermission: () => {
      return can(GraduationCategory.actions.list, GraduationCategory.subject);
    },
    generatePath: opt => graduationCategoryPages.path
  },
  {
    name: "sidebar.graduation_students",
    icon: "chevron_right",
    groupName: "graduation",
    hasPermission: () => {
      return can(GraduationStudents.actions.list, GraduationStudents.subject);
    },
    generatePath: opt => graduationStudentsPages.path
  },
  /* Survey */
  {
    name: "sidebar.survey",
    icon: "event_note",
    hasPermission: userRole => {
      return (
        can(Survey.actions.list, Survey.subject) ||
        hasValidRole(userRole, Role.Teacher)
      );
    },
    generatePath: opt => surveyPages.path
  },

  /* Report */
  {
    name: "sidebar.report",
    icon: "equalizer",
    group: "report",
    hasPermission: userRole => {
      const userInfo = store.getters["user/info"];
      const isAuthorized = userInfo.inputterOrReviewDetail?.authorized;
      return (
        (can(IndividualAssessment.actions.view, IndividualAssessment.subject) ||
          can(CommunicationLog.actions.view, CommunicationLog.subject) ||
          can(OverallAssessment.actions.view, OverallAssessment.subject) ||
          can(ScoreReport.actions.view, ScoreReport.subject) ||
          can(
            ExtendedExportation.actions.export,
            ExtendedExportation.subject
          ) ||
          can(ProgressReport.actions.setting, ProgressReport.subject) ||
          can(ProgressReport.actions.totalReport, ProgressReport.subject) ||
          can(ProgressReport.actions.submittedReport, ProgressReport.subject) ||
          can(
            ProgressReport.actions.censorSubmittedReport,
            ProgressReport.subject
          ) ||
          isAuthorized ||
          can(AttendanceReport.actions.view, AttendanceReport.subject)) &&
        !hasValidRole(userRole, Role.Family)
      );
    },
    generatePath: opt => individualAssessment.path
  },
  {
    name: "sidebar.individual_assessment",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: () => {
      return (
        can(IndividualAssessment.actions.view, IndividualAssessment.subject) ||
        can(Student.actions.view, Student.subject)
      );
    },
    generatePath: opt => individualAssessment.path
  },
  {
    name: "sidebar.communication_log",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: userRole => {
      return (
        (can(CommunicationLog.actions.view, CommunicationLog.subject) ||
          can(Student.actions.view, Student.subject)) &&
        !hasValidRole(userRole, Role.Student)
      );
    },
    generatePath: opt => communicationLog.path
  },
  {
    name: "sidebar.overall_assessment",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: () => {
      return can(OverallAssessment.actions.view, OverallAssessment.subject);
    },
    generatePath: opt => overallAssessment.path
  },
  {
    name: "sidebar.extended_exportation",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: () => {
      return can(
        ExtendedExportation.actions.export,
        ExtendedExportation.subject
      );
    },
    generatePath: opt => extendedExportationPage.path
  },
  {
    name: "sidebar.score_report",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: () => {
      return can(ScoreReport.actions.view, ScoreReport.subject);
    },
    generatePath: opt => scoreReportPages.path
  },
  {
    name: "sidebar.attendance_report",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: () => {
      return can(AttendanceReport.actions.view, AttendanceReport.subject);
    },
    generatePath: opt => attendanceReportPages.path
  },
  {
    name: "sidebar.progress_report",
    icon: "chevron_right",
    groupName: "report",
    hasPermission: () => {
      const userInfo = store.getters["user/info"];
      const isAuthorized = userInfo.inputterOrReviewDetail?.authorized;
      return (
        can(ProgressReport.actions.setting, ProgressReport.subject) ||
        can(ProgressReport.actions.totalReport, ProgressReport.subject) ||
        can(ProgressReport.actions.submittedReport, ProgressReport.subject) ||
        can(
          ProgressReport.actions.censorSubmittedReport,
          ProgressReport.subject
        ) ||
        isAuthorized
      );
    },
    generatePath: () => progressReportPages.path
  },

  {
    name: "sidebar.task_management",
    icon: "assignment",
    hasPermission: userRole =>
      !hasValidRole(userRole, [Role.Student, Role.Family]),
    generatePath: opt => taskManagementPage.path
  },

  /* Setting */
  {
    name: "sidebar.settings",
    icon: "settings",
    group: "settings",
    hasPermission: userRole => {
      return (
        hasValidRole(userRole, [Role.Admin, Role.Principal]) ||
        can(RolePage.actions.list, RolePage.subject)
      );
    },
    generatePath: opt => schoolParameterPages.path
  },
  {
    name: "sidebar.school_setting",
    icon: "chevron_right",
    groupName: "settings",
    hasPermission: () => {
      return can(All.actions, All.subject);
    },
    generatePath: opt => schoolParameterPages.path
  },
  {
    name: "sidebar.action_log",
    icon: "chevron_right",
    groupName: "settings",
    hasPermission: () => {
      return can(All.actions, All.subject);
    },
    generatePath: opt => actionLogsPage.path
  },
  {
    name: "sidebar.role",
    icon: "chevron_right",
    groupName: "settings",
    hasPermission: () => {
      return can(RolePage.actions.list, RolePage.subject);
    },
    generatePath: opt => rolePages.path
  },

  /* Setup for Role Family */
  {
    name: "sidebar.individual_assessment",
    icon: "border_all",
    hasPermission: userRole => {
      return hasValidRole(userRole, Role.Family);
    },
    generatePath: opt => individualAssessment.path
  },
  {
    name: "sidebar.score_report",
    icon: "pie_chart",
    hasPermission: userRole => {
      return hasValidRole(userRole, Role.Family);
    },
    generatePath: opt => scoreReportPages.path
  },
  {
    name: "sidebar.attendance_report",
    icon: "table_chart",
    hasPermission: userRole => {
      return hasValidRole(userRole, Role.Family);
    },
    generatePath: opt => attendanceReportPages.path
  }
];
